import type { ServiceProvider } from '@/types';

export interface PremiumPlan {
  id: string;
  name: string;
  price: number;
  duration: 'monthly' | 'yearly';
  features: string[];
  popular?: boolean;
}

export interface PremiumFeatures {
  accessToJobs: boolean;
  priorityNotifications: boolean;
  unlimitedQuotes: boolean;
  advancedAnalytics: boolean;
  customerInsights: boolean;
  premiumBadge: boolean;
  prioritySupport: boolean;
  leadGeneration: boolean;
}

class PremiumService {
  private premiumPlans: PremiumPlan[] = [
    {
      id: 'basic',
      name: 'Basic',
      price: 0,
      duration: 'monthly',
      features: [
        'Profile listing',
        'Receive notifications (after premium providers)',
        'Basic customer communication',
        'Standard support'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 29,
      duration: 'monthly',
      popular: true,
      features: [
        'Access to all customer job postings',
        'Priority job notifications (first access)',
        'Unlimited quote submissions',
        'Premium badge on profile',
        'Customer contact details',
        'Basic analytics dashboard',
        'Priority customer support'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 79,
      duration: 'monthly',
      features: [
        'Everything in Premium',
        'Advanced analytics & insights',
        'Lead generation tools',
        'Customer behavior insights',
        'Bulk quote management',
        'API access for integrations',
        'Dedicated account manager',
        'Custom branding options'
      ]
    }
  ];

  // Get all available plans
  getPlans(): PremiumPlan[] {
    return this.premiumPlans;
  }

  // Get plan by ID
  getPlan(planId: string): PremiumPlan | null {
    return this.premiumPlans.find(plan => plan.id === planId) || null;
  }

  // Check if provider is premium
  isPremiumProvider(provider: ServiceProvider): boolean {
    return Boolean(provider.isPremium) && 
           Boolean(provider.premiumExpiryDate) && 
           provider.premiumExpiryDate! > new Date();
  }

  // Get premium features for a provider
  getPremiumFeatures(provider: ServiceProvider): PremiumFeatures {
    const isPremium = this.isPremiumProvider(provider);
    const isEnterprise = isPremium && provider.subscriptionTier === 'ENTERPRISE';

    return {
      accessToJobs: isPremium,
      priorityNotifications: isPremium,
      unlimitedQuotes: isPremium,
      advancedAnalytics: isEnterprise,
      customerInsights: isEnterprise,
      premiumBadge: isPremium,
      prioritySupport: isPremium,
      leadGeneration: isEnterprise
    };
  }

  // Upgrade provider to premium
  upgradeToPremium(
    providerId: string, 
    planId: string, 
    duration: 'monthly' | 'yearly' = 'monthly'
  ): { success: boolean; message: string; expiryDate?: Date } {
    const plan = this.getPlan(planId);
    
    if (!plan || plan.id === 'basic') {
      return { success: false, message: 'Invalid premium plan selected' };
    }

    // Calculate expiry date
    const expiryDate = new Date();
    if (duration === 'monthly') {
      expiryDate.setMonth(expiryDate.getMonth() + 1);
    } else {
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    }

    // In real app, this would process payment and update database
    console.log(`🎉 Provider ${providerId} upgraded to ${plan.name} plan`);
    
    return {
      success: true,
      message: `Successfully upgraded to ${plan.name} plan!`,
      expiryDate
    };
  }

  // Get premium benefits description
  getPremiumBenefits(): string[] {
    return [
      '🎯 First access to all customer job postings',
      '⚡ Priority notifications before basic providers',
      '💼 Unlimited quote submissions per month',
      '⭐ Premium badge on your profile',
      '📊 Analytics dashboard with job insights',
      '🎧 Priority customer support',
      '📞 Direct customer contact information',
      '🚀 Higher visibility in search results'
    ];
  }

  // Check days remaining in premium subscription
  getDaysRemaining(provider: ServiceProvider): number {
    if (!this.isPremiumProvider(provider) || !provider.premiumExpiryDate) {
      return 0;
    }

    const now = new Date();
    const expiry = provider.premiumExpiryDate;
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }

  // Get subscription status
  getSubscriptionStatus(provider: ServiceProvider): {
    status: 'active' | 'expired' | 'none';
    plan: string;
    daysRemaining: number;
    expiryDate?: Date;
  } {
    const isPremium = this.isPremiumProvider(provider);
    const daysRemaining = this.getDaysRemaining(provider);

    if (isPremium) {
      return {
        status: 'active',
        plan: provider.subscriptionTier || 'PREMIUM',
        daysRemaining,
        expiryDate: provider.premiumExpiryDate
      };
    } else if (provider.premiumExpiryDate && provider.premiumExpiryDate < new Date()) {
      return {
        status: 'expired',
        plan: provider.subscriptionTier || 'BASIC',
        daysRemaining: 0,
        expiryDate: provider.premiumExpiryDate
      };
    } else {
      return {
        status: 'none',
        plan: 'BASIC',
        daysRemaining: 0
      };
    }
  }

  // Mock payment processing
  processPayment(_planId: string, _paymentMethod: string): Promise<{ success: boolean; transactionId?: string; error?: string }> {
    return new Promise((resolve) => {
      // Simulate payment processing delay
      setTimeout(() => {
        // Mock success (in real app, integrate with Stripe/PayPal)
        const success = Math.random() > 0.1; // 90% success rate for demo
        
        if (success) {
          resolve({
            success: true,
            transactionId: 'txn_' + Date.now() + Math.random().toString(36).substring(2, 9)
          });
        } else {
          resolve({
            success: false,
            error: 'Payment failed. Please check your payment method and try again.'
          });
        }
      }, 2000);
    });
  }
}

export const premiumService = new PremiumService();